import { Component, computed, effect, inject, Input, signal } from '@angular/core';
import { InspectionItemDto, InspectionService, SortDto, TenantDetailDto } from '@artemis-software/wr-api';
import { loader } from '@/utils/signals/loader';
import { Tracked } from '@/utils/signals/tracked';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs';

@Component({
  standalone: false,
  selector: 'wr-tenant-dashboard-inspection-overview',
  templateUrl: './tenant-dashboard-inspection-overview.component.html',
  styleUrls: ['./tenant-dashboard-inspection-overview.component.scss'],
})
export class TenantDashboardInspectionOverviewComponent {

  inspectionService = inject(InspectionService);
  formBuilder = inject(FormBuilder);

  @Input()
  @Tracked()
  tenantId!: string;

  form!: FormGroup;

  isLoading = loader();

  @Tracked() page = 0;
  @Tracked() pageSize = 10;
  @Tracked() sort: SortDto = { sort: 'fromTimestamp', order: 'DESC' };

  readonly getInspectionLink = (inspection: InspectionItemDto) => (['inspection', inspection.id]);

  results = asyncComputed(() => {
    if (!this.tenantId) {
      return undefined;
    }
    this.reload();
    return this.inspectionService.findAllPaginated({
      tenantId: this.tenantId,
      status: this.status(),
      page: this.page,
      size: this.pageSize,
      sort: {
        sort: this.sort.sort,
        order: this.sort.order,
      },
      fullTextSearch: this.form.get('fullTextSearch')?.value ?? undefined,
    });
  }, undefined, this.isLoading);

  inspections = computed(() => this.results()?.items ?? []);

  inspectionCount = computed(() => this.results()?.totalItems ?? 0);

  reload = signal(0);

  status = signal(InspectionItemDto.StatusEnum.Sent);

  constructor() {
    this.form = this.formBuilder.group({
      fullTextSearch: this.formBuilder.control(''),
      status: this.formBuilder.control(InspectionItemDto.StatusEnum.Sent),
    });
    effect(() => {
      this.form.valueChanges.pipe(debounceTime(200)).subscribe((value) => {
        if (this.status() !== value.status) {
          this.status.set(value.status);
        }
        this.reload.update((n) => n + 1);
      });
    });
  }

  protected readonly TenantDetailDto = TenantDetailDto;
}
